// components/thabat/InKindCreationModal.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Agriculture as CropIcon,
  Build as ServiceIcon,
  RequestQuote as NeedIcon,
  VolunteerActivism as DonateIcon
} from '@mui/icons-material';

const InKindCreationModal = ({ open, onClose, itemType }) => {
  const theme = useTheme();
  const isNeed = itemType === 'need';

  const handleCreateCrop = () => {
    onClose();
    // For crops/offers - both donations and needs use the same creation component
    // but with different default offer_type
    const path = isNeed ? '#/in-kind-offers/create?type=need' : '#/in-kind-offers/create?type=donate';
    window.location.href = path;
  };

  const handleCreateService = () => {
    onClose();
    // For services - separate routes for donations and needs
    const path = isNeed ? '#/in-kind-services/create' : '#/in-kind-services/create';
    window.location.href = path;
  };

  const title = isNeed ? 'Create In-Kind Request' : 'Create In-Kind Donation';
  const icon = isNeed ? <NeedIcon /> : <DonateIcon />;
  
  const cropTitle = isNeed ? 'Request Crops & Products' : 'Donate Crops & Products';
  const cropDescription = isNeed 
    ? 'Request agricultural products, crops, seeds, or other farm-related items you need'
    : 'Donate agricultural products, crops, seeds, or other farm-related items';
  
  const serviceTitle = isNeed ? 'Request Services' : 'Donate Services';
  const serviceDescription = isNeed
    ? 'Request services like transportation, storage, labor, or other professional services you need'
    : 'Donate services like transportation, storage, labor, or other professional services';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
          backdropFilter: "blur(10px)",
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        pb: 2
      }}>
        <Typography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
          {icon}
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          What type of in-kind {isNeed ? 'request' : 'donation'} would you like to create?
        </Typography>
        
        <Grid container spacing={3}>
          {/* Crop Option */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.05),
                }
              }}
              onClick={handleCreateCrop}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <CropIcon 
                  sx={{ 
                    fontSize: 60, 
                    color: theme.palette.success.main,
                    mb: 2
                  }} 
                />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {cropTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cropDescription}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="primary.main" fontWeight="medium">
                    Click to continue →
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Option */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.05),
                }
              }}
              onClick={handleCreateService}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <ServiceIcon 
                  sx={{ 
                    fontSize: 60, 
                    color: theme.palette.info.main,
                    mb: 2
                  }} 
                />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {serviceTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {serviceDescription}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="primary.main" fontWeight="medium">
                    Click to continue →
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        p: 2,
        justifyContent: 'flex-end'
      }}>
        <MuiButton onClick={onClose} color="inherit">
          Cancel
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default InKindCreationModal;